import type { SharedValue } from "react-native-reanimated";
import { Easing, useAnimatedStyle, withTiming } from "react-native-reanimated";
import { StyleSheet, UnistylesRuntime } from "react-native-unistyles";

import {
  EPG_COMPACT_ROW_HEIGHT,
  EPG_SURFACE_OPACITY,
} from "$src/core/constants/epg-grid";
import useAnimatedTheme from "$src/core/hooks/use-animated-theme";
import opacity from "$src/core/utils/color/opacity";
import CustomPlatform from "$src/core/utils/platform/platform";

/** Figma `border/default` = white @ 10% — used for dividers, card border and the progress track. */
const BORDER_OPACITY = 0.1;
/** Figma muted time column. */
const TIME_OPACITY = 0.6;

const stylesheet = StyleSheet.create(({ colors, spacing, border }) => {
  const rowHeight = CustomPlatform.select({
    tv: spacing(14),
    default: EPG_COMPACT_ROW_HEIGHT,
  });

  return {
    compactRow: {
      flexDirection: "row",
      alignItems: "center",
      gap: spacing(4),
      // Padding, not margin: the divider spans the full column and only the text insets (Figma).
      paddingHorizontal: spacing(4),
      height: rowHeight,
      marginHorizontal: CustomPlatform.select({
        tv: spacing(1),
        default: 0,
      }),
      borderBottomWidth: border.widths.sm,
      borderBottomColor: opacity(colors.foregroundDefault, BORDER_OPACITY),
      // Static half of the focus outline: only the colour needs a worklet.
      ...CustomPlatform.select({
        tv: {
          outlineStyle: "solid" as const,
          outlineWidth: border.widths.md,
          outlineOffset: border.widths.md,
          outlineColor: "transparent",
        },
        default: {},
      }),
      _web: {
        _focus: {
          outline: "none",
        },
        "_focus-visible": {
          outlineStyle: "solid",
          outlineColor: colors.backgroundFocus,
          outlineWidth: border.widths.md,
          outlineOffset: -border.widths.md,
        },
      },
    },
    liveCard: {
      marginVertical: spacing(2),
      marginHorizontal: CustomPlatform.select({
        tv: spacing(1),
        default: 0,
      }),
      borderRadius: border.radius.md,
      overflow: "visible",
      ...CustomPlatform.select({
        tv: {
          outlineStyle: "solid" as const,
          outlineWidth: border.widths.md,
          outlineOffset: border.widths.md,
          outlineColor: "transparent",
        },
        default: {},
      }),
      _web: {
        _focus: {
          outline: "none",
        },
        "_focus-visible": {
          outlineStyle: "solid",
          outlineColor: colors.backgroundFocus,
          outlineWidth: border.widths.md,
          outlineOffset: border.widths.sm,
          borderRadius: border.radius.md,
        },
      },
    },
    liveCardInner: {
      overflow: "hidden",
      borderRadius: border.radius.md,
      // TV keeps the CMS surface; phone/web use the subtle Figma tint over the page.
      backgroundColor: CustomPlatform.select({
        tv: colors.backgroundSurface,
        default: opacity(colors.foregroundDefault, EPG_SURFACE_OPACITY),
      }),
      // Phone has no card border; web and TV do.
      borderWidth: CustomPlatform.select({
        native: 0,
        default: border.widths.sm,
      }),
      borderColor: opacity(colors.foregroundDefault, BORDER_OPACITY),
    },
    imageWrap: {
      width: "100%",
      height: spacing(34),
      overflow: "hidden",
    },
    image: {
      ...StyleSheet.absoluteFillObject,
    },
    imageScrim: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: opacity(colors.backgroundDefault, 0.45),
    },
    imageGradient: {
      position: "absolute",
      left: 0,
      right: 0,
      bottom: 0,
      height: spacing(24),
    },
    playButton: {
      position: "absolute",
      top: "50%",
      left: "50%",
      width: spacing(11),
      height: spacing(11),
      transform: [{ translateX: -spacing(5.5) }, { translateY: -spacing(5.5) }],
      alignItems: "center",
      justifyContent: "center",
      borderRadius: border.radius.full,
      backgroundColor: colors.backgroundFocus,
    },
    playIcon: {
      width: spacing(6),
      height: spacing(6),
      color: colors.foregroundOnInverted,
    },
    bottomOverlay: {
      position: "absolute",
      left: 0,
      right: 0,
      bottom: 0,
      paddingHorizontal: spacing(4),
      paddingBottom: spacing(2),
      gap: spacing(2),
    },
    badgeRow: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
    },
    liveBadge: {
      paddingVertical: spacing(0.75),
      paddingHorizontal: spacing(1.5),
      borderRadius: border.radius.xs,
      backgroundColor: colors.backgroundNegative,
    },
    liveBadgeText: {
      color: colors.foregroundOnNegative,
    },
    remainingBadge: {
      paddingVertical: spacing(0.75),
      paddingHorizontal: spacing(1.5),
      borderRadius: border.radius.sm,
      backgroundColor: colors.backgroundInverted,
    },
    remainingBadgeText: {
      color: colors.foregroundOnInverted,
    },
    progressTrack: {
      height: spacing(1),
      borderRadius: border.radius.xs,
      backgroundColor: opacity(colors.foregroundDefault, BORDER_OPACITY),
      overflow: "hidden",
    },
    progressFill: {
      height: "100%",
      borderRadius: border.radius.xs,
      backgroundColor: colors.backgroundNegative,
    },
    metaRow: {
      flexDirection: "row",
      alignItems: "center",
      gap: spacing(4),
      paddingHorizontal: spacing(4),
      height: rowHeight,
    },
    time: {
      flexGrow: 0,
      flexShrink: 0,
      width: spacing(16),
      color: opacity(colors.foregroundDefault, TIME_OPACITY),
      fontVariant: ["tabular-nums"],
    },
    title: {
      flex: 1,
      minWidth: 0,
    },
    past: {
      opacity: 0.45,
    },
    menuIcon: {
      width: spacing(6),
      height: spacing(6),
      color: colors.foregroundDefault,
    },
  };
});

const FOCUS_TIMING = { duration: 150, easing: Easing.out(Easing.ease) };

/** One mapper per row, not two: an EPG page mounts hundreds, and ADR 0001 counts mappers per node. */
export const useProgramBlockAnimatedStyles = ({
  isFocused,
  isLive,
}: {
  isFocused: SharedValue<boolean>;
  isLive: boolean;
}) => {
  const animatedTheme = useAnimatedTheme();

  const focusStyle = useAnimatedStyle(() => {
    const { backgroundFocus, backgroundFloat } = animatedTheme.value.colors;
    const focused = isFocused.value;

    return {
      outlineColor: withTiming(
        focused ? backgroundFocus : "transparent",
        FOCUS_TIMING,
      ),
      // The live card is already floating; only the compact row tints on focus.
      backgroundColor: withTiming(
        isLive || focused ? backgroundFloat : "transparent",
        FOCUS_TIMING,
      ),
    };
  });

  return { focusStyle };
};

const initialTheme = UnistylesRuntime.getTheme();

export const IMAGE_DIMENSIONS = {
  live: [{ height: initialTheme.spacing(34) }],
};

export default stylesheet;
